/* eslint-disable import/prefer-default-export, import/no-named-as-default,
  @typescript-eslint/no-unused-vars */
import React from 'react';
import { toast } from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import QueryKeys from '../config';

import API from '../../../network';
import { ApplyJobRequestBody } from './types';

const useJobs = (props?: { enabled?: boolean }) => {
  const { enabled } = props || { enabled: true };
  const {
    isLoading, data, error, refetch,
  } = useQuery({
    queryKey: QueryKeys.JOBS,
    queryFn: async () => API.getInstance().getJobs().then((response) => response.data),
    refetchOnWindowFocus: false,
    initialData: [],
    enabled,
  });

  const jobsOptions = React.useMemo(
    () => data?.map((option) => ({ label: `${option.jobId} - ${option.jobTitle}`, value: option.jobOrderId })) || [],
    [data],
  );

  return {
    jobsLoading: isLoading,
    jobsOptions,
    data,
    error,
    fetchJobs: refetch,
  };
};

const useActiveJobs = (props?: { enabled?: boolean }) => {
  const { enabled } = props || { enabled: true };
  const [customLoading, setCustomLoading] = React.useState(true);
  const {
    isLoading, data, error, refetch,
  } = useQuery({
    queryKey: ['activeJobs'],
    queryFn: async () => {
      setCustomLoading(true);
      try {
        const response = await API.getInstance().getActiveJobIds();
        return response.data;
      } finally {
        setCustomLoading(false);
      }
    },
    refetchOnWindowFocus: false,
    initialData: [],
    enabled,
  });

  React.useEffect(() => {
    if (error) setCustomLoading(false);
  }, [error]);

  const activeJobsOptions = React.useMemo(
    () => data?.map((option) => ({ label: `${option.jobId} - ${option.jobTitle}`, value: option.jobOrderId })) || [],
    [data],
  );

  return {
    activeJobsLoading: isLoading || customLoading,
    activeJobsOptions,
    data,
    error,
    fetchActiveJobs: refetch,
  };
};

const useApplyJob = () => {
  const {
    mutateAsync, error, status, isPending,
  } = useMutation({
    mutationFn: async (data: ApplyJobRequestBody) => API.getInstance().applyJob(data),
  });

  const applyJob = async (
    data: ApplyJobRequestBody,
  ) => new Promise((resolve) => {
    const promise = mutateAsync(data).then(async () => {
      resolve(true);
    });
    toast.promise(promise, {
      loading: 'Please wait...',
      success: 'Job applied successfully',
      error: (err: any) => `${err?.response?.data || err?.response?.data?.message || error || 'something went wrong!'}`,
    }, {
      position: 'bottom-center',
    });
  });

  return {
    applyJob,
    isPending,
    error,
    status,
  };
};

const useJobBooleanSearchString = (jobOrderId?: number) => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = React.useState(false);

  const {
    data,
    error,
    status,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['jobBooleanSearch', jobOrderId],
    queryFn: async ({ queryKey }) => {
      const [unused, id] = queryKey;
      if (!id) return null;
      const response = await API.getInstance().getResumeSearchString(id as number);
      return response;
    },
    enabled: !!jobOrderId,
  });

  const fetchById = async (id: number) => {
    setLoading(true);
    try {
      const result = await queryClient.fetchQuery({
        queryKey: ['jobBooleanSearch', id],
        queryFn: async () => {
          const response = await API.getInstance().getResumeSearchString(id);
          return response;
        },
      });
      return result?.booleanSearchString || result as unknown as string;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to fetch boolean search string');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    fetchById,
    jobBooleanSearchLoading: isLoading || isFetching || loading,
    error,
    status,
    booleanSearchString: data?.booleanSearchString,
  };
};

const useJob = (jobOrderId?: string) => {
  const queryClient = useQueryClient();

  const {
    data: jobDetails,
    error,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['job', jobOrderId],
    queryFn: async () => {
      if (!jobOrderId) return null;
      return API.getInstance().fetchJobDetails(jobOrderId);
    },
    enabled: !!jobOrderId,
  });

  const fetchJob = async (id: string) => {
    try {
      return await queryClient.fetchQuery({
        queryKey: ['job', id],
        queryFn: async () => API.getInstance().fetchJobDetails(id),
      });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to fetch job details');
      throw err;
    }
  };

  return {
    jobDetails,
    isLoading: isLoading || isFetching,
    error,
    fetchJob,
    refetch,
  };
};

export {
  useApplyJob,
  useJobs,
  useActiveJobs,
  useJobBooleanSearchString,
  useJob,
};
